import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import appConfig from "config/appConfig";
import {
  toastAddErrorMessageConfig,
  toastAddSuccessMessageConfig,
} from "config/toastConfig";
import { verifyTokenExpire } from "middleware/verifyToken";
import { ToastBottomEnd } from "views/components/Toast";

const defaultData = {
  name: "",
  acronyme: "",
  iso_code: "",
  country_id: "",
  logo: "",
};

const defaultError = {
  name: [],
  acronyme: [],
  iso_code: [],
  country_id: [],
  logo: [],
};

export const useInstitutionForm = () => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    data: defaultData,
    errors: defaultError,
    loading: true,
    submitting: false,
    logo: undefined,
    countries: [],
    institutionCountry: null,
  });

  const updateState = useCallback((updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  const fetchInstitution = useCallback(async () => {
    if (!verifyTokenExpire()) return;

    try {
      updateState({ loading: true });
      const response = await axios.get(
        `${appConfig.apiDomaine}/my/institutions`
      );

      const institutionData = {
        name: response.data.institution?.name ?? "",
        acronyme: response.data.institution?.acronyme ?? "",
        iso_code: response.data.institution?.iso_code ?? "",
        country_id: response.data.institution?.country_id ?? "",
        logo: response.data.institution?.logo ?? "",
      };

      updateState({
        data: institutionData,
        loading: false,
        errors: defaultError,
        countries: response.data?.countries ?? [],
        institutionCountry: response.data.institution?.country ?? null,
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching institution:", error);
      updateState({
        loading: false,
        errors: {
          ...defaultError,
          general: [t("Erreur lors du chargement des données")],
        },
      });
      return null;
    }
  }, [updateState, t]);

  const updateField = useCallback((fieldName, value) => {
    setState((prevState) => ({
      ...prevState,
      data: {
        ...prevState.data,
        [fieldName]: value,
      },
      errors: {
        ...prevState.errors,
        [fieldName]: [],
      },
    }));
  }, []);

  const handleFileUpload = useCallback(
    (file) => {
      if (!file) return;

      updateState({ logo: file });
      updateField("logo", file);

      const reader = new FileReader();
      reader.onload = function(e) {
        const image = document.getElementById("Image1");
        if (image) {
          image.src = e.target.result;
        }
      };
      reader.readAsDataURL(file);
    },
    [updateField, updateState]
  );

  const submitForm = useCallback(async () => {
    if (!verifyTokenExpire()) return false;

    try {
      updateState({ submitting: true, errors: defaultError });

      const formData = new FormData();
      if (state.logo) {
        formData.append("logo", state.data.logo);
      }
      formData.set("name", state.data.name);
      formData.set("acronyme", state.data.acronyme);
      formData.set("iso_code", state.data.iso_code);
      formData.set("country_id", state.data.country_id);
      formData.append("_method", "put");

      await axios.post(`${appConfig.apiDomaine}/my/institutions`, formData);

      updateState({ submitting: false });
      ToastBottomEnd.fire(toastAddSuccessMessageConfig());
      return true;
    } catch (error) {
      const errorData = error.response?.data?.error || {};
      updateState({
        submitting: false,
        errors: { ...defaultError, ...errorData },
      });
      ToastBottomEnd.fire(toastAddErrorMessageConfig());
      return false;
    }
  }, [state.data, state.logo, updateState]);

  useEffect(() => {
    fetchInstitution();
  }, [fetchInstitution]);

  return {
    data: state.data,
    errors: state.errors,
    loading: state.loading,
    submitting: state.submitting,
    countries: state.countries,
    institutionCountry: state.institutionCountry,
    updateField,
    handleFileUpload,
    submitForm,
    refetch: fetchInstitution,
  };
};
